import React, { useMemo } from "react";
import { Clock, FileText, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useGetMonthlyOvertimeBalanceQuery,
  useGetOvertimeWithStatusQuery,
} from "../../slices/overtimeApiSlice";

export default function OvertimeContent() {
  const navigate = useNavigate();

  // Fetch monthly balance data and overtime records
  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    error: balanceError,
  } = useGetMonthlyOvertimeBalanceQuery();
  const { data: overtimeData, isLoading: isOvertimeLoading } =
    useGetOvertimeWithStatusQuery();

  // Calculate status breakdown
  const statusBreakdown = useMemo(() => {
    if (!overtimeData || !Array.isArray(overtimeData)) {
      return { pending: 0, forwarded: 0, approved: 0 };
    }

    const breakdown = {
      pending: 0,
      forwarded: 0,
      approved: 0,
    };

    overtimeData.forEach((item) => {
      const status = item.status?.toUpperCase() || "";
      const hours = item.details?.totalHours || 0;

      if (status === "PENDING") {
        breakdown.pending += hours;
      } else if (status === "FORWARDED") {
        breakdown.forwarded += hours;
      } else if (status === "APPROVED") {
        breakdown.approved += hours;
      }
    });

    return breakdown;
  }, [overtimeData]);

  // Extract values with fallbacks
  const monthlyLimit = balanceData?.monthlyLimit || 0;
  const approvedHours = balanceData?.approvedHours || 0;
  const remainingBalance =
    balanceData?.remainingBalance || monthlyLimit - approvedHours;
  const progressPercentage =
    monthlyLimit > 0 ? (approvedHours / monthlyLimit) * 100 : 0;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 h-full">
      {/* Left Column - Overtime Balance */}
      <div className="bg-white border-r py-6 px-8 h-full">
        <h2 className="text-2xl font-bold text-zinc-800 mb-2">Overtime</h2>
        <p className="text-zinc-600 mb-8">Check your overtime balance</p>

        {/* Monthly Limit Card */}
        <div className="border border-zinc-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <Clock className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-zinc-800">
              Monthly Overtime Limit
            </h3>
          </div>

          {isBalanceLoading || isOvertimeLoading ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="text-zinc-600 text-sm mt-2">Loading...</p>
            </div>
          ) : balanceError ? (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">
                Failed to load overtime balance
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-zinc-600">Total Limit:</span>
                <span className="font-semibold text-zinc-800">
                  {monthlyLimit.toFixed(1)} hrs
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-600">Used (Approved):</span>
                <span className="font-semibold text-zinc-800">
                  {approvedHours.toFixed(1)} hrs
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-600">Remaining</span>
                <span
                  className={`font-semibold ${
                    remainingBalance > 0 ? "text-blue-600" : "text-red-600"
                  }`}
                >
                  {Math.max(0, remainingBalance).toFixed(1)} hrs
                </span>
              </div>

              {/* Progress Bar */}
              <div className="pt-2">
                <div className="w-full bg-zinc-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      progressPercentage > 100
                        ? "bg-red-600"
                        : progressPercentage >= 80
                          ? "bg-yellow-500"
                          : "bg-blue-600"
                    }`}
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Usage percentage */}
              <div className="text-center pt-2">
                <span className="text-xs text-zinc-600">
                  {progressPercentage.toFixed(1)}% used
                </span>
              </div>
            </div>
          )}
        </div>

        {/* How it works */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <svg
              className="w-5 h-5 text-zinc-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-zinc-800">
              How it works
            </h3>
          </div>
          <ol className="space-y-3 text-zinc-600">
            <li className="flex gap-3">
              <span className="font-semibold">1.</span>
              <span>
                Technician submits an overtime request with details and
                category.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-semibold">2.</span>
              <span>
                Supervisor reviews and forwards to the manager with an optional
                note.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="font-semibold">3.</span>
              <span>
                Manager approves or rejects, with visibility into who forwarded
                which employee and full overtime details.
              </span>
            </li>
          </ol>
        </div>
      </div>

      {/* Right Column - Other Options */}
      <div className="bg-white py-6 px-8 h-full">
        <h2 className="text-2xl font-bold text-zinc-800 mb-2">Other options</h2>
        <p className="text-zinc-600 mb-6">
          A quick link to other options you can do for today
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* My Overtime Records */}
          <button
            onClick={() => navigate("/technician/overtime/records")}
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#CCB1E3] p-3 rounded-lg">
                <FileText className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  My Overtime Records
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Check your overtime history and your balance.
                </p>
              </div>
            </div>
          </button>

          {/* Apply Overtime */}
          <button
            onClick={() => navigate("/technician/overtime/apply")}
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#F2B5D4] p-3 rounded-lg">
                <Clock className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Apply Overtime
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  Worked overtime? Apply your application here.
                </p>
              </div>
            </div>
          </button>

          {/* Monthly Limit Details */}
          <button
            onClick={() => navigate("/technician/overtime/monthly-limit")}
            className="bg-white border border-zinc-200 rounded-lg p-4 hover:shadow-md hover:border-zinc-300 transition-all text-left flex justify-start items-start"
          >
            <div className="flex items-start justify-start space-x-4">
              <div className="bg-[#B9E5DD] p-3 rounded-lg">
                <Clock className="w-5 h-5 text-black stroke-[1.5]" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm tracking-tight text-zinc-800 mb-1">
                  Monthly Limit Details
                </h3>
                <p className="text-sm leading-tight text-zinc-500">
                  View breakdown of your monthly overtime limit usage.
                </p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
